import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useContacts } from "@/hooks/use-contacts";
import { useOrganizationDeals } from "@/hooks/use-contact-deals";
import { useOrganizationAssignedDevices } from "@/hooks/use-organization-assigned-devices";
import { ContactDealsList } from "./ContactDealsList";
import { StatsTab } from "./StatsTab";
import { ContactModal } from "./ContactModal";
import { OrganizationDevicesTab } from "./OrganizationDevicesTab";
import { Building2, Globe, MapPin, Users, Mail, Phone, Camera, Edit, FileText, BarChart3, UserPlus, Copy, ExternalLink, Eye, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { ShowcaseQRCode } from "@/components/organization/ShowcaseQRCode";
import { OrganizationQuestionnaire } from "./OrganizationQuestionnaire";
import { useShowcasePageViews } from "@/hooks/use-showcase-page-views";
interface OrganizationDetailModalProps {
  organization: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: () => void;
}
export const OrganizationDetailModal = ({
  organization,
  open,
  onOpenChange,
  onEdit
}: OrganizationDetailModalProps) => {
  const {
    data: contacts = [],
    refetch: refetchContacts
  } = useContacts();
  const {
    data: organizationDeals = [],
    isLoading: dealsLoading
  } = useOrganizationDeals(organization.id);
  const {
    data: organizationDevices = []
  } = useOrganizationAssignedDevices(organization.id);
  const { data: pageViewStats } = useShowcasePageViews(
    organization.visibility === 'public' ? organization.id : ''
  );

  // Filter contacts that belong to this organization
  const organizationContacts = contacts.filter(contact => contact.organization_id === organization.id);
  const getRelationshipBadgeColor = (status: string) => {
    switch (status) {
      case 'Customer':
        return 'bg-green-100 text-green-800';
      case 'Prospect':
        return 'bg-blue-100 text-blue-800';
      case 'Partner':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] overflow-hidden flex flex-col [&>button]:text-slate-700 [&>button]:hover:text-slate-900">
        <DialogHeader className="bg-white -mx-6 -mt-6 px-6 py-4 rounded-t-xl border-b border-slate-200">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Organization Details
            </DialogTitle>
            {onEdit && <Button onClick={onEdit} variant="outline" size="sm" className="bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90 border-[#DFEF97] my-0 mx-[22px]">
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>}
          </div>
        </DialogHeader>
        
        <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="contacts">
              <Users className="h-4 w-4 mr-2" />
              Contacts ({organizationContacts.length})
            </TabsTrigger>
            <TabsTrigger value="deals">
              <FileText className="h-4 w-4 mr-2" />
              Deals ({organizationDeals.length})
            </TabsTrigger>
            <TabsTrigger value="devices">
              <Camera className="h-4 w-4 mr-2" />
              Devices ({organizationDevices.length})
            </TabsTrigger>
            <TabsTrigger value="questionnaire">
              <ClipboardList className="h-4 w-4 mr-2" />
              Questionnaire
            </TabsTrigger>
            <TabsTrigger value="stats">
              <BarChart3 className="h-4 w-4 mr-2" />
              Stats
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="details" className="flex-1 overflow-auto">
            <div className="space-y-6 p-6">
              {/* Organization Header */}
              <div className="flex items-start gap-4">
                {organization.logo_url ? <img src={organization.logo_url} alt={organization.name} className="w-16 h-16 rounded-lg object-cover" /> : <div className="w-16 h-16 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Building2 className="h-8 w-8 text-teal-600" />
                  </div>}
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-slate-800">{organization.name}</h2>
                  {organization.company_id && <p className="text-lg text-slate-600 mt-1">{organization.company_id}</p>}
                  <div className="flex gap-2 mt-2">
                    <Badge className={getRelationshipBadgeColor(organization.relationship_status)}>
                      {organization.relationship_status}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Organization Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800">Company Information</h3>
                  
                  {organization.industry && <div className="flex items-center gap-3 text-slate-600">
                      <Users className="h-5 w-5" />
                      <div>
                        <div className="text-sm text-slate-500">Industry</div>
                        <div className="font-medium">{organization.industry}</div>
                      </div>
                    </div>}
                  
                  {organization.website && <div className="flex items-center gap-3 text-slate-600">
                      <Globe className="h-5 w-5" />
                      <div>
                        <div className="text-sm text-slate-500">Website</div>
                        <a href={organization.website.startsWith('http') ? organization.website : `https://${organization.website}`} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline">
                          {organization.website}
                        </a>
                      </div>
                    </div>}
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800">Location & Contact</h3>
                  
                  {organization.country && <div className="flex items-center gap-3 text-slate-600">
                      <MapPin className="h-5 w-5" />
                      <div>
                        <div className="text-sm text-slate-500">Country</div>
                        <div className="font-medium">{organization.country}</div>
                      </div>
                    </div>}
                  
                  {organization.address && <div className="flex items-start gap-3 text-slate-600">
                      <MapPin className="h-5 w-5 mt-0.5" />
                      <div>
                        <div className="text-sm text-slate-500">Address</div>
                        <div className="font-medium">{organization.address}</div>
                      </div>
                    </div>}

                  {organization.division && <div className="space-y-2">
                      <div className="text-sm text-slate-500">Division</div>
                      <div className="font-medium">{organization.division}</div>
                    </div>}
                </div>
              </div>

              {/* Notes */}
              {organization.notes && <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800">Notes</h3>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-slate-700 whitespace-pre-wrap">{organization.notes}</p>
                  </div>
                </div>}

              {/* Public Showcase */}
              {organization.visibility === 'public' && (organization.slug || organization.id) && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-slate-800">Public Showcase</h3>
                    <Badge variant="secondary" className="bg-green-100 text-green-800">
                      <Eye className="h-3 w-3 mr-1" />
                      Public
                    </Badge>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                    <p className="text-sm text-slate-600">
                      This organization's cameras are publicly visible. Share this link to allow public access.
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-4 items-start">
                      <div className="flex gap-2">
                        <Input
                          readOnly
                          value={`${window.location.origin}/showcase/${organization.slug || organization.id}`}
                          className="font-mono text-sm bg-white"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/showcase/${organization.slug || organization.id}`);
                            toast.success("Showcase URL copied to clipboard");
                          }}
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copy
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/showcase/${organization.slug || organization.id}`, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          View
                        </Button>
                      </div>
                      <ShowcaseQRCode 
                        organizationId={organization.slug || organization.id}
                        organizationName={organization.name}
                        size={200}
                      />
                    </div>
                    {pageViewStats && (
                      <div className="grid grid-cols-3 gap-3 mt-4">
                        <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-slate-800">{pageViewStats.totalViews}</div>
                          <div className="text-xs text-slate-500">Total Views</div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-slate-800">{pageViewStats.uniqueVisitors}</div>
                          <div className="text-xs text-slate-500">Unique Visitors</div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-slate-800">{pageViewStats.last7Days}</div>
                          <div className="text-xs text-slate-500">Last 7 Days</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                <div className="space-y-2">
                  <div className="text-sm text-slate-500">Created</div>
                  <div className="text-sm font-medium">
                    {new Date(organization.created_at).toLocaleDateString()} at {new Date(organization.created_at).toLocaleTimeString()}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-slate-500">Last Updated</div>
                  <div className="text-sm font-medium">
                    {new Date(organization.updated_at).toLocaleDateString()} at {new Date(organization.updated_at).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="contacts" className="flex-1 overflow-auto">
            <div className="space-y-4 p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Contacts at {organization.name}</h3>
                <ContactModal
                  prefilledOrganizationId={organization.id}
                  onContactCreated={refetchContacts}
                  triggerButton={
                    <Button className="bg-[hsl(60,73%,76%)] text-black border border-gray-300 hover:bg-[hsl(60,73%,70%)]">
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Contact
                    </Button>
                  }
                />
              </div>
              {organizationContacts.length === 0 ? <div className="text-center py-8">
                  <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600">No contacts found for this organization.</p>
                </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {organizationContacts.map(contact => <div key={contact.id} className="p-4 border rounded-lg bg-white shadow-sm">
                      <div className="flex items-start gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={contact.profile_image} />
                          <AvatarFallback className="bg-slate-100 text-slate-600">
                            {contact.first_name?.charAt(0)}{contact.last_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800">
                            {contact.first_name} {contact.last_name}
                          </div>
                          <div className="text-sm text-slate-600 truncate flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {contact.email}
                          </div>
                          {contact.job_title && <div className="text-sm text-slate-500">{contact.job_title}</div>}
                          {contact.phone && <div className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                              <Phone className="h-3 w-3" />
                              {contact.phone}
                            </div>}
                          {contact.relationship_status && <Badge className={`mt-2 ${getRelationshipBadgeColor(contact.relationship_status)}`}>
                              {contact.relationship_status}
                            </Badge>}
                        </div>
                      </div>
                    </div>)}
                </div>}
            </div>
          </TabsContent>

          <TabsContent value="deals" className="flex-1 overflow-auto">
            <ContactDealsList deals={organizationDeals} isLoading={dealsLoading} title="Organization Deals" showContact={true} showAssignee={true} />
          </TabsContent>

          <TabsContent value="devices" className="flex-1 overflow-auto">
            <OrganizationDevicesTab 
              organizationId={organization.id} 
              organizationName={organization.name} 
            />
          </TabsContent>

          <TabsContent value="questionnaire" className="flex-1 overflow-auto">
            <OrganizationQuestionnaire
              organizationId={organization.id}
              organizationName={organization.name}
            />
          </TabsContent>

          <TabsContent value="stats" className="flex-1 overflow-auto">
            <StatsTab deals={organizationDeals} isLoading={dealsLoading} title={`${organization.name} Statistics`} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>;
};